import { useState, useEffect, useCallback } from 'react'
import { setDocumentTitle } from 'shared/util'
import { To, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  Box,
  Stack,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material'

import { Notifications as NotificationsIcon } from '@mui/icons-material';

import { useTenant } from 'features/tenant/context'

import { DoLogin, ChangePassword, DoGroupLogin } from 'interface/login'
import { LoginResult, GroupLoginResult, OrganizedGroup } from 'interface/login/types'
import { CountOpenNotification } from 'interface/notification'

import { GetTenantBackground, GetTenantBanner } from 'interface/tenant'
import { TenantResult } from 'interface/tenant/types'

import { ErrorInfo } from 'components/controls/errorInfo'

import {
  changePasswordFormSchema,
  loginFormSchema,
  selectGroupFormSchema,
} from './validation'
import {
  ChangePasswordFormValues,
  LoginFormValues,
  SelectGroupFormValues,
} from './types'

import { LoginForm } from './components/loginForm'
import { ChangePasswordForm } from './components/changePassword'
import { SelectGroupForm } from './components/selectGroup'
import { OpenNotificationDialog } from 'features/notification/dialog/listOpen'

type LoginProps = {
  onSubmit: (
    tenant: TenantResult,
    loginResult: LoginResult,
    groupLoginResult: GroupLoginResult,
    from: To | undefined
  ) => void
}
export const Login = ({
  onSubmit
}: LoginProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))

  const tenant = useTenant()
  const location = useLocation()
  const from: To | undefined = location.state?.from

  const loginForm = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
    resolver: yupResolver(loginFormSchema),
  })
  const changePasswordForm = useForm<ChangePasswordFormValues>({
    defaultValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    mode: 'onChange',
    resolver: yupResolver(changePasswordFormSchema),
  })

  const selectGroupForm = useForm<SelectGroupFormValues>({
    defaultValues: {
      group: -1,
    },
    mode: 'onChange',
    resolver: yupResolver(selectGroupFormSchema),
  })

  const [modal, setModal] = useState<'notifications' | null>(null)
  const [tenantBackground, setTenantBackground] = useState<string | undefined>()
  const [tenantBanner, setTenantBanner] = useState<string | undefined>()
  const [tenantResourceError, setTenantResourceError] = useState<Error | undefined>()
  const [openNotificationCount, setOpenNotificationCount] = useState<number>(0)
  const [needChangePassword, setNeedChangePassword] = useState<boolean>(false)
  const [changePassword, setChangePassword] = useState<boolean>(false)

  const [loginError, setLoginError] = useState<Error | null>(null)
  const [loginResult, setLoginResult] = useState<LoginResult | null>(null)
  const [groups, setGroups] = useState<OrganizedGroup[]>([])

  const onLogin = loginForm.handleSubmit(({ email, password }) => {
    login(email, password)
  })

  const login = (email: string, password: string) => {
    setLoginError(null)
    DoLogin(tenant.domain, {
      email,
      password,
    })
      .then(result => {
        setGroups(result.groups)
        setLoginResult(result)
        if (result.needChangePassword) {
          setNeedChangePassword(true)
          setChangePassword(true)
        }
        if (changePassword) {
          // -> パスワード変更
        } else if (result.groups.length > 0) {
          selectGroupForm.setValue('group', result.groups[0].groupId)
          if (result.groups.length === 1) {
            groupLogin(result, result.groups[0].groupId)
          }
        }
      })
      .catch(setLoginError)
  }

  const onChangePassword = changePasswordForm.handleSubmit((data) => {
    if (!loginResult) return
    const loginToken = loginResult.loginToken
    if (!loginToken) return
    if (!data.newPassword) return
    ChangePassword(loginToken, { password: data.newPassword })
      .then(() => {
        setChangePassword(false)
        setNeedChangePassword(false)
        if (loginResult.groups.length > 0) {
          selectGroupForm.setValue('group', loginResult.groups[0].groupId)
          if (loginResult.groups.length === 1) {
            groupLogin(loginResult, loginResult.groups[0].groupId)
          }
        }
      })
      .catch(setLoginError)
  })
  const onChangePasswordCancel = () => {
    setLoginResult(null)
    setChangePassword(false)
    setNeedChangePassword(false)
    setGroups([])
    selectGroupForm.setValue('group', -1)
  }

  const onGroupCancel = () => {
    setLoginResult(null)
    setChangePassword(false)
    setNeedChangePassword(false)
    setGroups([])
    selectGroupForm.setValue('group', -1)
  }
  const onGroupLogin = selectGroupForm.handleSubmit(({ group }) => {
    if (!loginResult) return
    groupLogin(loginResult, group)
  })
  const groupLogin = useCallback(
    (loginResult: LoginResult, groupId: number) => {
      const loginToken = loginResult.loginToken
      if (!loginToken) return

      DoGroupLogin(loginToken, { groupId })
        .then((res) => onSubmit(tenant, loginResult, res, from))
        .catch((reason) => setLoginError(reason))
    },
    [onSubmit, tenant, from]
  )

  useEffect(
    () => setDocumentTitle(
      loginResult === null ? 'ログイン'
      : changePassword ? 'パスワード変更'
      : 'グループ選択'
    ),
    [tenant, loginResult, changePassword]
  )

  useEffect(() => {
    GetTenantBackground(tenant.domain)
      .catch(reason => setTenantResourceError(reason))
      .then(response => setTenantBackground(
        response ? URL.createObjectURL(response.blob) : undefined
      ))

    GetTenantBanner(tenant.domain)
      .catch(reason => setTenantResourceError(reason))
      .then(response => setTenantBanner(
        response ? URL.createObjectURL(response.blob) : undefined
      ))
    
    CountOpenNotification(tenant.domain)
      .catch(reason => setTenantResourceError(reason))
      .then(response => setOpenNotificationCount(response ?? 0))
  }, [tenant.domain])

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={isSmallMedia ? undefined : {
        backgroundColor: 'GhostWhite',
        height: '100vh'
      }}
    >
      <Card
        square={isSmallMedia}
        sx={{
          position: "relative",
          height: isSmallMedia ? "100vh" : undefined
        }}
      >
        <Stack
          direction={isSmallMedia ? "column" : "row"}
          height={isSmallMedia ? "100%" : undefined}
          position="relative"
        >
          {tenantResourceError ? (
            <ErrorInfo error={tenantResourceError} padding={1} />
          ) : (
            <CardMedia
              image={tenantBackground}
              component="img"
              title={tenant.tenantName}
              sx={isSmallMedia ? {
                flexGrow: 1
              } : {
                width: '60vh',
                maxWidth: '100%'
              }}
            />
          )}
          <CardContent sx={{ minWidth: '12em' }}>
            <Stack direction="column" height="100%" spacing={1}>
              <Box flexGrow={1} textAlign="center">
                {tenantBanner && (
                  <img src={tenantBanner} alt={tenant.tenantName} />
                )}
                {loginResult === null ? (
                  <Typography component="div" variant="body1">
                    ログイン
                  </Typography>
                ) : changePassword ? (
                  <Typography component="div" variant="body1">
                    パスワード変更
                  </Typography>
                ) : (
                  <Typography component="div" variant="body1">
                    グループ選択
                  </Typography>
                )}
              </Box>

              {loginError ? (
                <Alert severity="error">{loginError.message}</Alert>
              ) : needChangePassword ? (
                <Alert severity="warning">パスワードの変更が必要です</Alert>
              ) : (
                <></>
              )}

              {loginResult === null ? (
                <LoginForm
                  domain={tenant.domain}
                  control={loginForm.control}
                  isChangePassword={changePassword}
                  onLogin={onLogin}
                  setChangePassword={setChangePassword}
                />
              ) : changePassword ? (
                <ChangePasswordForm
                  control={changePasswordForm.control}
                  isValid={changePasswordForm.formState.isValid}
                  onChangePassword={onChangePassword}
                  onChangePasswordCancel={onChangePasswordCancel}
                />
              ) : (
                <SelectGroupForm
                  control={selectGroupForm.control}
                  isValid={selectGroupForm.formState.isValid}
                  groups={groups}
                  onGroupLogin={onGroupLogin}
                  onGroupCancel={onGroupCancel}
                />
              )}
            </Stack>
          </CardContent>
        </Stack>

        { openNotificationCount > 0 && (
          <Box
            position="absolute"
            top={isSmallMedia ? 0 : undefined}
            bottom={isSmallMedia ? undefined : 0}
            left={0}
            margin={2}
          >
            <Chip
              icon={<NotificationsIcon/>}
              variant="filled"
              color="info"
              label={`最新のお知らせが ${openNotificationCount} 件あります`}
              sx={{
                cursor: 'pointer'
              }}
              onClick={() => setModal("notifications")}
            />
          </Box>
        )}
      </Card>

      { modal === "notifications" && (
        <OpenNotificationDialog
          domain={tenant.domain}
          onClose={() => setModal(null)}
        />
      )}
    </Stack>
  )
}
